<script>
import parse from 'date-fns/parse';
import differenceInMinutes from 'date-fns/differenceInMinutes';
import { generateTimeSlots } from '../helpers/businessHour';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import NextSelect from 'dashboard/components-next/select/Select.vue';

const timeSlots = generateTimeSlots(30);
const MIDNIGHT = '12:00 AM';
const DEFAULT_BREAK = { breakFrom: '12:00 PM', breakTo: '01:00 PM' };
const NO_BREAK = { breakFrom: '', breakTo: '' };

const toDate = value => parse(value, 'hh:mm a', new Date());

// Fechamento depois da abertura. '12:00 AM' como fechamento é aceito como
// "fim do dia", regra herdada da versão sem pausa.
const isRangeValid = ({ from, to }) => {
  if (!from || !to) return false;
  if (to === MIDNIGHT) return true;
  return differenceInMinutes(toDate(to), toDate(from)) > 0;
};

// Pausa estritamente dentro do expediente e com início antes do fim. Sem pausa
// (os dois vazios) é sempre válido; só um preenchido é inválido.
const isBreakValid = ({ from, to, breakFrom, breakTo, openAllDay }) => {
  if (!breakFrom && !breakTo) return true;
  if (openAllDay || !breakFrom || !breakTo) return false;
  const fromDate = toDate(from);
  const breakFromDate = toDate(breakFrom);
  const breakToDate = toDate(breakTo);
  const closesAtMidnight = to === MIDNIGHT;
  return (
    differenceInMinutes(breakFromDate, fromDate) > 0 &&
    differenceInMinutes(breakToDate, breakFromDate) > 0 &&
    (closesAtMidnight || differenceInMinutes(toDate(to), breakToDate) > 0)
  );
};

const groupByPeriod = slots =>
  ['AM', 'PM']
    .map(period => ({
      label: period,
      options: slots
        .filter(s => s.endsWith(period))
        .map(s => ({ value: s, label: s })),
    }))
    .filter(g => g.options.length);

export default {
  components: {
    Icon,
    NextSelect,
  },
  props: {
    dayName: {
      type: String,
      required: true,
    },
    timeSlot: {
      type: Object,
      default: () => ({
        from: '',
        to: '',
        breakFrom: '',
        breakTo: '',
      }),
    },
  },
  emits: ['update'],
  computed: {
    fromTimeSlots() {
      return groupByPeriod(timeSlots);
    },
    toTimeSlots() {
      return groupByPeriod(timeSlots.filter(slot => slot !== MIDNIGHT));
    },
    isDayEnabled: {
      get() {
        return Boolean(this.timeSlot.from && this.timeSlot.to);
      },
      set(value) {
        const newSlot = value
          ? {
              ...this.timeSlot,
              from: timeSlots[0],
              to: timeSlots[16],
              ...NO_BREAK,
              valid: true,
              openAllDay: false,
            }
          : {
              ...this.timeSlot,
              from: '',
              to: '',
              ...NO_BREAK,
              valid: false,
              openAllDay: false,
            };
        this.$emit('update', newSlot);
      },
    },
    fromTime: {
      get() {
        return this.timeSlot.from;
      },
      set(value) {
        this.emitSlot({ from: value });
      },
    },
    toTime: {
      get() {
        return this.timeSlot.to;
      },
      set(value) {
        this.emitSlot({ to: value });
      },
    },
    hasBreak: {
      get() {
        return Boolean(this.timeSlot.breakFrom && this.timeSlot.breakTo);
      },
      set(value) {
        this.emitSlot(value ? { ...DEFAULT_BREAK } : { ...NO_BREAK });
      },
    },
    breakFromTime: {
      get() {
        return this.timeSlot.breakFrom;
      },
      set(value) {
        this.emitSlot({ breakFrom: value });
      },
    },
    breakToTime: {
      get() {
        return this.timeSlot.breakTo;
      },
      set(value) {
        this.emitSlot({ breakTo: value });
      },
    },
    fromDate() {
      return toDate(this.fromTime);
    },
    toDate() {
      return toDate(this.toTime);
    },
    breakMinutes() {
      if (!this.hasBreak || !isBreakValid(this.timeSlot)) return 0;
      return differenceInMinutes(
        toDate(this.timeSlot.breakTo),
        toDate(this.timeSlot.breakFrom)
      );
    },
    totalHours() {
      if (this.timeSlot.openAllDay) return '24h';

      const totalMinutes =
        differenceInMinutes(this.toDate, this.fromDate) - this.breakMinutes;
      const [h, m] = [Math.floor(totalMinutes / 60), totalMinutes % 60];

      return [h && `${h}h`, m && `${m}m`].filter(Boolean).join(' ') || '0m';
    },
    hasRangeError() {
      return !isRangeValid(this.timeSlot);
    },
    hasBreakError() {
      return !isBreakValid(this.timeSlot);
    },
    hasError() {
      return !this.timeSlot.valid;
    },
    isOpenAllDay: {
      get() {
        return this.timeSlot.openAllDay;
      },
      set(value) {
        if (value) {
          this.$emit('update', {
            ...this.timeSlot,
            from: '12:00 AM',
            to: '11:59 PM',
            ...NO_BREAK,
            valid: true,
            openAllDay: value,
          });
        } else {
          this.$emit('update', {
            ...this.timeSlot,
            from: '09:00 AM',
            to: '05:00 PM',
            ...NO_BREAK,
            valid: true,
            openAllDay: value,
          });
        }
      },
    },
  },
  methods: {
    // Toda mudança de horário passa por aqui para que `valid` sempre reflita o
    // conjunto (expediente + pausa) e não só o campo que acabou de mudar.
    emitSlot(patch) {
      const next = { ...this.timeSlot, ...patch };
      this.$emit('update', {
        ...next,
        valid: isRangeValid(next) && isBreakValid(next),
      });
    },
  },
};
</script>

<template>
  <tr>
    <td class="ltr:pl-4 ltr:pr-3 rtl:pl-3 rtl:pr-4">
      <div class="flex items-center gap-2 min-h-16">
        <input
          v-model="isDayEnabled"
          name="enable-day"
          class="m-0"
          type="checkbox"
          :title="$t('INBOX_MGMT.BUSINESS_HOURS.DAY.ENABLE')"
        />
        <span class="text-body-main text-n-slate-12 font-medium">
          {{ dayName }}
        </span>
      </div>
    </td>
    <td class="py-3 ltr:pr-3 rtl:pl-3">
      <div v-if="isDayEnabled" class="flex flex-col gap-1.5">
        <div class="flex items-center gap-4">
          <div class="flex items-center gap-2">
            <input
              v-model="isOpenAllDay"
              name="enable-open-all-day"
              class="m-0"
              type="checkbox"
              :title="$t('INBOX_MGMT.BUSINESS_HOURS.ALL_DAY')"
            />
            <span class="text-body-main text-n-slate-12">{{
              $t('INBOX_MGMT.BUSINESS_HOURS.ALL_DAY')
            }}</span>
          </div>
          <NextSelect
            v-model="fromTime"
            :groups="fromTimeSlots"
            :placeholder="$t('INBOX_MGMT.BUSINESS_HOURS.DAY.CHOOSE')"
            :disabled="isOpenAllDay"
          />
          <div class="flex items-center">
            <Icon icon="i-lucide-minus size-4" />
          </div>
          <NextSelect
            v-model="toTime"
            :groups="toTimeSlots"
            :placeholder="$t('INBOX_MGMT.BUSINESS_HOURS.DAY.CHOOSE')"
            :disabled="isOpenAllDay"
          />
        </div>
        <div v-if="!isOpenAllDay" class="flex items-center gap-4">
          <div class="flex items-center gap-2">
            <input
              v-model="hasBreak"
              name="enable-break"
              class="m-0"
              type="checkbox"
              :title="$t('INBOX_MGMT.BUSINESS_HOURS.BREAK')"
            />
            <span class="text-body-main text-n-slate-12">{{
              $t('INBOX_MGMT.BUSINESS_HOURS.BREAK')
            }}</span>
          </div>
          <template v-if="hasBreak">
            <NextSelect
              v-model="breakFromTime"
              :groups="toTimeSlots"
              :placeholder="$t('INBOX_MGMT.BUSINESS_HOURS.DAY.CHOOSE')"
            />
            <div class="flex items-center">
              <Icon icon="i-lucide-minus size-4" />
            </div>
            <NextSelect
              v-model="breakToTime"
              :groups="toTimeSlots"
              :placeholder="$t('INBOX_MGMT.BUSINESS_HOURS.DAY.CHOOSE')"
            />
          </template>
        </div>
        <span v-if="hasRangeError" class="error text-label-small text-n-ruby-9">
          {{ $t('INBOX_MGMT.BUSINESS_HOURS.DAY.VALIDATION_ERROR') }}
        </span>
        <span
          v-else-if="hasBreakError"
          class="error text-label-small text-n-ruby-9"
        >
          {{ $t('INBOX_MGMT.BUSINESS_HOURS.DAY.BREAK_VALIDATION_ERROR') }}
        </span>
      </div>
      <span v-else class="text-body-main text-n-slate-11">
        {{ $t('INBOX_MGMT.BUSINESS_HOURS.DAY.UNAVAILABLE') }}
      </span>
    </td>
    <td class="py-3 ltr:pr-3 rtl:pl-3">
      <span
        v-if="isDayEnabled && !hasError"
        class="label bg-n-blue-3 text-n-blue-11 text-label-small inline-block px-2 py-1 rounded-lg cursor-default whitespace-nowrap"
      >
        {{ totalHours }}
      </span>
    </td>
  </tr>
</template>
